/**
 * User Data Hooks with React Query
 * ==================================
 *
 * React Query hooks for user profile and preferences management
 *
 * Features:
 * - Automatic caching
 * - Background refetching
 * - Optimistic updates
 * - Loading/error states managed by React Query
 *
 * Usage:
 *   const { data: profile, isLoading } = useUserProfile()
 *   const { mutate: updateProfile } = useUpdateProfile()
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  userApi,
  type LearningProfile,
  type OnboardingData,
} from '@/lib/api-client';
import { queryKeys } from '@/config/query-client';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface UserProfile {
  id: string;
  supabase_id: string;
  email: string;
  email_verified: boolean;
  username: string | null;
  full_name: string | null;
  avatar_url: string | null;
  bio: string | null;
  settings: UserSettings;
  onboarded: boolean;
  learning_profile: LearningProfile;
  created_at: string;
  updated_at: string;
  last_login: string | null;
}

export interface UserSettings {
  llm_provider: string;
  llm_model: string;
  preferred_language: string;
  timezone: string;
  notifications_enabled: boolean;
}

export interface ProfileUpdateData {
  username?: string;
  full_name?: string;
  bio?: string;
}

export interface SettingsUpdateData {
  llm_provider?: string;
  llm_model?: string;
  preferred_language?: string;
  timezone?: string;
  notifications_enabled?: boolean;
}

// ============================================================================
// QUERY HOOKS (Fetching Data)
// ============================================================================

/**
 * Get user profile
 *
 * Automatically cached and refetched in background
 */
export function useUserProfile() {
  return useQuery<UserProfile>({
    queryKey: queryKeys.user.profile(),
    queryFn: async () => {
      return await userApi.getProfile();
    },
    // Refetch every 5 minutes to keep data fresh
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Get user preferences/settings
 *
 * Automatically cached
 */
export function useUserPreferences() {
  return useQuery<UserSettings>({
    queryKey: queryKeys.user.preferences(),
    queryFn: async () => {
      return await userApi.getPreferences();
    },
    // Settings rarely change, keep fresh for 10 minutes
    staleTime: 10 * 60 * 1000,
  });
}

// ============================================================================
// MUTATION HOOKS (Updating Data)
// ============================================================================

/**
 * Update user profile
 *
 * Automatically invalidates and refetches profile on success
 */
export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ProfileUpdateData) => {
      return await userApi.updateProfile(data);
    },
    onSuccess: updatedProfile => {
      // Update cache immediately with new data (optimistic update)
      queryClient.setQueryData(queryKeys.user.profile(), updatedProfile);

      // Also invalidate to refetch from server
      queryClient.invalidateQueries({ queryKey: queryKeys.user.profile() });
    },
    onError: (error: any) => {
      console.error('❌ Failed to update profile:', error);
    },
  });
}

/**
 * Complete first-time onboarding
 *
 * Persists the learning profile + flips `onboarded`. On success we prime the
 * profile cache with the server response so the OnboardingGate immediately
 * sees `onboarded: true` and stops redirecting into the wizard.
 */
export function useCompleteOnboarding() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: OnboardingData) => {
      return await userApi.completeOnboarding(data);
    },
    onSuccess: updatedProfile => {
      queryClient.setQueryData(queryKeys.user.profile(), updatedProfile);
      queryClient.invalidateQueries({ queryKey: queryKeys.user.profile() });
    },
    onError: (error: any) => {
      console.error('❌ Failed to complete onboarding:', error);
    },
  });
}

/**
 * Update user preferences
 *
 * Automatically invalidates and refetches preferences on success
 */
export function useUpdatePreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: SettingsUpdateData) => {
      return await userApi.updatePreferences(data);
    },
    onSuccess: updatedSettings => {
      // Update cache immediately
      queryClient.setQueryData(queryKeys.user.preferences(), updatedSettings);

      // Invalidate to refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.user.preferences() });
    },
    onError: (error: any) => {
      console.error('❌ Failed to update preferences:', error);
    },
  });
}

/**
 * Upload avatar
 *
 * Automatically refetches profile to show new avatar
 */
export function useUploadAvatar() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (file: File) => {
      return await userApi.uploadAvatar(file);
    },
    onSuccess: _result => {
      // Invalidate profile to refetch with new avatar_url
      queryClient.invalidateQueries({ queryKey: queryKeys.user.profile() });
    },
    onError: (error: any) => {
      console.error('❌ Failed to upload avatar:', error);
    },
  });
}

/**
 * Delete the current user's account
 *
 * Cascade-deletes user-owned data and removes the Supabase auth user.
 * Caller is responsible for signing out / redirecting after success.
 */
export function useDeleteAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      return await userApi.deleteAccount();
    },
    onSuccess: () => {
      queryClient.clear();
    },
    onError: (error: any) => {
      console.error('❌ Failed to delete account:', error);
    },
  });
}

/**
 * Sync user with backend (for AuthContext)
 *
 * Creates/fetches MongoDB user on app load
 */
export function useSyncUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (accessToken: string) => {
      // Call backend to create/fetch user
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/v1/users/me`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to sync user: ${response.status}`);
      }

      return await response.json();
    },
    onSuccess: backendUser => {
      // Cache the user profile
      queryClient.setQueryData(queryKeys.user.profile(), backendUser);
    },
    onError: error => {
      console.error('❌ Error syncing user with backend:', error);
    },
  });
}

// ============================================================================
// HELPER HOOKS
// ============================================================================

/**
 * Check if user profile is loaded
 */
export function useIsUserLoaded() {
  const { data, isLoading } = useUserProfile();
  return !isLoading && !!data;
}

/**
 * Get user's full name (with fallback)
 */
export function useUserName() {
  const { data: profile } = useUserProfile();
  return profile?.full_name || profile?.email || 'User';
}
